import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { Category } from '../_models/category.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../auth';
import { isEqual, uniqWith } from 'lodash-es';

@Injectable()
export class CategoryService {
  private authToken;
  private company_id;

  constructor(
    private auth: AuthService,
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.company_id = res.companyID));
  }

  createCategory(body: any) {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<Category>(url, body, { headers });
  }

  findCategories(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category`;
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const searchData = queryParams.filter.searchData;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);

    let fromString =
      `page=${pageIndex}&size=${pageSize}&search_term=${searchData}` +
      '&sort=' +
      `${queryParams.sortField},${queryParams.sortOrder}`;

    if (queryParams.filter.searchId) fromString += `&id_has=${queryParams.filter.searchId}`;
    if (queryParams.filter.searchName) fromString += `&name_has=${queryParams.filter.searchName}`;
    if (queryParams.filter.searchRole) fromString += `&role_id=${queryParams.filter.searchRole}`;
    if (queryParams.filter.searchInbox) fromString += `&inbox_id=${queryParams.filter.searchInbox}`;
    if (queryParams.filter.isActive) fromString += `&is_active=${queryParams.filter.isActive}`;
    const params = new HttpParams({ fromString });

    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  getCategoriesAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const { filter, sortOrder, sortField } = queryParams;
    let fromString = '';

    if (filter.searchData) fromString += `&search_term=${filter.searchData}`;
    if (filter.searchId) fromString += `&id_has=${filter.searchId}`;
    if (filter.searchName) fromString += `&name_has=${filter.searchName}`;
    if (filter.searchRole) fromString += `&role_id=${filter.searchRole}`;
    if (filter.searchInbox) fromString += `&inbox_id=${filter.searchInbox}`;
    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;

    const params = new HttpParams({ fromString });

    return this.http.get(
      `${environment.USER_BASE}/companies/${this.company_id}/category/csv?sort=${sortField},${sortOrder}`,
      {
        params,
        responseType: 'text',
      },
    );
  }

  getCategories() {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category?sort=name,asc`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({ fromString: `size=500` });
    return this.http.get<any>(url, { headers, params });
  }

  getCategoriesInTemplateByUserType(userType, term: string): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let params = new HttpParams();
    params = params.append('term', term);

    if (userType === 'admin') params = params.append('all_categories', 'true');
    if (userType === 'manager') params = params.append('manager', 'true');
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }
  updateCategory(_category: Category): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/${_category.id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      name: _category.name,
      roles: _category.roles,
      managers: _category.managers,
      email: _category.email,
      fax_number: _category.fax_number,
      inbox_id: _category.inbox_id,
      has_intervention_permission: _category.has_intervention_permission,
      is_default_intervention_team: _category.is_default_intervention_team,
      is_active: _category.is_active,
    };
    return this.http.put<Category>(url, body, { headers });
  }

  updateStatusCategory(category, isActive) {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/${category.id}/toggle-active`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = isActive;
    return this.http.put<any>(url, { headers, params });
  }

  searchCategories(term: string, template, hasInterventionPermission?): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let params = new HttpParams();
    params = params.append('term', term);
    if (template) {
      params = params.append('has_template', 'true');
    }
    if (hasInterventionPermission?.toString()) {
      params = params.append('has_intervention_permission', hasInterventionPermission);
    }

    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  getAllCategories(term: string): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let params = new HttpParams();
    params = params.append('term', term);
    params = params.append('all_categories', 'true');
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  searchFilteredCategories(filter: any, searchTerm: string): Observable<QueryResultsModel> {
    let fromString = `term=${searchTerm || ''}`;

    if (filter.all) fromString += '&all_categories=true';
    if (filter.inboxActivated) fromString += '&inbox_activated=true';

    const params = new HttpParams({ fromString });

    return this.http.get<QueryResultsModel>(
      `${environment.USER_BASE}/companies/${this.company_id}/category/autocomplete`,
      { params },
    );
  }

  searchCategoriesWithTemplate(term: string): Observable<any> {
    return this.searchCategories(term, true);
  }

  searchCategoriesWithoutInterventionPermission(userType, term: string): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let params = new HttpParams();
    params = params.append('term', term);
    params = params.append('has_template', 'true');
    params = params.append('has_intervention_permission', 'false');
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  getCategoriesByWorkflow(company, term: string): Observable<any> {
    const url = `${environment.USER_BASE}/companies/${company}/category/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({ fromString: 'term=' + term });
    return this.http.get<QueryResultsModel>(url, { headers, params });
  }

  getCategoryById(id) {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/${id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }
  getCategoryForWorkflowById(id, company) {
    const url = `${environment.USER_BASE}/companies/${company}/category/${id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  checkDefaultInterventionTeam() {
    const url = `${environment.USER_BASE}/companies/${this.company_id}/category/check-default`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }
  getTeamByIds(catIds) {
    let ids = '';
    catIds = uniqWith(catIds, isEqual);
    catIds.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    const url = environment.USER_BASE + `/companies/${this.company_id}/category/get-by-ids`;
    return this.http.get<any>(url, { params });
  }
}
